import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import AgentLog from "../../components/common/AgentLog";
import WeaveLoader from "../../components/common/WeaveLoader";
import { toolsService } from "../../services/tools";
import { formatCost, formatModel } from "../../utils/agentLog";
import { getApiErrorMessage } from "../../utils/axios";
import { timeStampToEasyTime } from "../../utils/time";

export const TOOL_DOMAIN_FINDER = "domain_finder";
export const TOOL_EMD = "emd_opportunities";

export const toolBasePath = (toolId) =>
  toolId === TOOL_EMD ? "/tools/emd/" : "/tools/domain-finder/";

export function availabilityLabel(summary) {
  if (!summary) return "";
  if (summary.availability_mode === "registrar") {
    const name = summary.registrar === "porkbun" ? "Porkbun" : "registrar";
    return `Availability confirmed via ${name}`;
  }
  return "Availability: DNS estimate (not registrar-confirmed)";
}

export function truncateText(text, max = 80) {
  const value = (text || "").trim();
  if (!value) return "";
  return value.length <= max ? value : `${value.slice(0, max - 3)}...`;
}

/** Title + bold grey count for tool report headers. */
export function ReportTitle({ title, countLabel }) {
  return (
    <div className="flex min-w-0 items-baseline gap-3">
      <p className="truncate text-sm font-medium text-theme-text" title={title}>
        {title}
      </p>
      <span className="shrink-0 text-sm font-bold text-theme-text opacity-45">
        {countLabel}
      </span>
    </div>
  );
}

export function downloadCsv(filename, rows, columns) {
  const escape = (value) => {
    const text = value == null ? "" : String(value);
    if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
    return text;
  };
  const header = columns.map((c) => escape(c.label)).join(",");
  const body = rows
    .map((row) => columns.map((c) => escape(c.value(row))).join(","))
    .join("\n");
  const blob = new Blob([[header, body].join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Main 3/4 + side 1/4 layout. */
export function ToolTwoColumn({ main, side }) {
  return (
    <div className="grid gap-6 lg:grid-cols-4">
      <div className="min-w-0 lg:col-span-3">{main}</div>
      <div className="min-w-0 lg:block">{side}</div>
    </div>
  );
}

export function useToolRun(toolId, runHash) {
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [runsVersion, setRunsVersion] = useState(0);
  const pollRef = useRef(null);
  const selectedHashRef = useRef(null);
  const base = toolBasePath(toolId);

  const clearPoll = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => () => clearPoll(), []);

  const poll = useCallback((hash) => {
    clearPoll();
    const tick = () => {
      toolsService.getRun(
        hash,
        (res) => {
          if (selectedHashRef.current !== hash) return;
          const data = res.data;
          setRun(data);
          const terminal =
            data.status === "succeeded" || data.status === "failed";
          if (terminal) {
            clearPoll();
            setRunsVersion((v) => v + 1);
          }
        },
        (err) => {
          if (selectedHashRef.current !== hash) return;
          clearPoll();
          setError(getApiErrorMessage(err, "Failed to load run"));
        },
      );
    };
    // First refresh soon, then every 5s while still queued/running.
    pollRef.current = setInterval(tick, 5000);
  }, []);

  // Load / poll when the URL hash changes.
  useEffect(() => {
    clearPoll();
    setError("");
    selectedHashRef.current = runHash || null;
    if (!runHash) {
      setRun(null);
      setLoading(false);
      setSubmitting(false);
      return undefined;
    }
    setLoading(true);
    setRun(null);
    toolsService.getRun(
      runHash,
      (res) => {
        if (selectedHashRef.current !== runHash) return;
        setRun(res.data);
        setLoading(false);
        setSubmitting(false);
        if (res.data.status === "queued" || res.data.status === "running") {
          poll(runHash);
        }
      },
      (err) => {
        if (selectedHashRef.current !== runHash) return;
        setLoading(false);
        setSubmitting(false);
        setError(getApiErrorMessage(err, "Failed to load run"));
      },
    );
    return () => clearPoll();
  }, [runHash, poll]);

  const start = (input) => {
    setError("");
    setSubmitting(true);
    toolsService.createRun(
      toolId,
      input,
      (res) => {
        setRunsVersion((v) => v + 1);
        navigate(`${base}${res.data.hash}/`);
      },
      (err) => {
        setSubmitting(false);
        setError(getApiErrorMessage(err, "Failed to start run"));
      },
    );
  };

  const retry = () => {
    if (!run?.input || submitting) return;
    start(run.input);
  };

  const openRun = (hash) => navigate(`${base}${hash}/`);
  const newRun = () => navigate(base);

  const isActive =
    Boolean(run) &&
    (run.status === "queued" || run.status === "running");

  return {
    run,
    error,
    submitting,
    loading,
    start,
    retry,
    openRun,
    newRun,
    runsVersion,
    isActive,
  };
}

/** Plain spinner while fetching a past run (no log flash). */
export function RunLoading() {
  return (
    <div className="flex justify-center py-12">
      <WeaveLoader size={40} />
    </div>
  );
}

function AnimatedDots() {
  const frames = [".", "..", "...", ""];
  const [step, setStep] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setStep((s) => (s + 1) % frames.length), 400);
    return () => clearInterval(id);
  }, []);
  return (
    <span aria-hidden="true" className="inline-block w-4 text-left">
      {frames[step]}
    </span>
  );
}

/** Total run cost: LLM + DataForSEO credits (when tracked). */
export function toolRunCostUsd(usage) {
  if (!usage) return 0;
  return (
    Number(usage.llm?.cost_usd || 0) + Number(usage.dataforseo_cost_usd || 0)
  );
}

/** Staff-only meta for AgentLog (same build_cost flag as change requests). */
export function toolRunLogMeta(run, showBuildCost) {
  if (!showBuildCost) return "";
  return [formatModel(run?.model), formatCost(toolRunCostUsd(run?.usage))]
    .filter(Boolean)
    .join(" · ");
}

/** Live progress + AgentLog for queued/running/failed, or logs-only after success. */
export function RunProgress({
  run,
  showBuildCost = false,
  onRetry,
  retrying = false,
}) {
  const active = run?.status === "queued" || run?.status === "running";
  const failed = run?.status === "failed";
  const succeeded = run?.status === "succeeded";
  const lines = run?.agent_log || [];
  if (!active && !failed && !(succeeded && lines.length)) return null;

  const meta = toolRunLogMeta(run, showBuildCost);

  return (
    <div className="space-y-4">
      {active && (
        <div className="flex items-center gap-3">
          <WeaveLoader size={36} />
          <div>
            <p className="text-sm font-medium text-theme-text">
              {run?.status === "queued" ? "Queued" : "Running"}
            </p>
            <p className="text-xs text-theme-text opacity-50">
              This can take a minute. Hang tight
              <AnimatedDots />
            </p>
          </div>
        </div>
      )}
      {failed && (
        <div className="rounded-xl border border-theme-secondary p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-theme-text">Run failed</p>
              {run.error && (
                <p className="mt-1 text-sm text-theme-text opacity-70 break-words">
                  {run.error}
                </p>
              )}
            </div>
            {onRetry ? (
              <button
                type="button"
                onClick={onRetry}
                disabled={retrying}
                className="shrink-0 rounded-lg bg-theme-primary px-3 py-1.5 text-xs font-medium text-theme-button disabled:opacity-40"
              >
                {retrying ? "Retrying…" : "Retry"}
              </button>
            ) : null}
          </div>
        </div>
      )}
      {(active || failed || lines.length > 0) && (
        <AgentLog
          lines={lines}
          live={active}
          meta={meta}
          className="text-left"
        />
      )}
    </div>
  );
}

const isInFlight = (status) => status === "queued" || status === "running";

const PAST_RUNS_PAGE = 20;

export function PastRunsSidebar({
  toolId,
  activeHash,
  onOpen,
  onNew,
  refreshKey = 0,
}) {
  const [runs, setRuns] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAST_RUNS_PAGE);
  const pollRef = useRef(null);

  const clearPoll = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const applyRuns = (list) => {
    setRuns(list);
    setLoaded(true);
    const anyActive = list.some((r) => isInFlight(r.status));
    if (!anyActive) {
      clearPoll();
      return;
    }
    if (pollRef.current) return;
    pollRef.current = setInterval(() => {
      toolsService.listRuns(
        toolId,
        (res) => {
          const data = res.data;
          const next = Array.isArray(data) ? data : data.results || [];
          setRuns(next);
          if (!next.some((r) => isInFlight(r.status))) {
            clearPoll();
          }
        },
        () => clearPoll(),
      );
    }, 5000);
  };

  useEffect(() => {
    clearPoll();
    setLoaded(false);
    setVisibleCount(PAST_RUNS_PAGE);
    toolsService.listRuns(
      toolId,
      (res) => {
        const data = res.data;
        applyRuns(Array.isArray(data) ? data : data.results || []);
      },
      () => {
        setRuns([]);
        setLoaded(true);
      },
    );
    return () => clearPoll();
  }, [toolId, refreshKey]);

  return (
    <aside className="space-y-6">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-theme-secondary-text">
          Past runs
        </h2>
        {activeHash && (
          <button
            type="button"
            onClick={onNew}
            className="text-xs font-medium text-theme-primary hover:opacity-80"
          >
            New run
          </button>
        )}
      </div>
      {!loaded ? (
        <p className="text-xs text-theme-text opacity-50">Loading…</p>
      ) : runs.length === 0 ? (
        <p className="text-xs text-theme-text opacity-50">No runs yet.</p>
      ) : (
        <>
          <ul className="space-y-2">
            {runs.slice(0, visibleCount).map((item) => {
              const active = item.hash === activeHash;
              const ts = item.create_date
                ? timeStampToEasyTime(Date.parse(item.create_date) / 1000)
                : "";
              return (
                <li key={item.hash}>
                  <button
                    type="button"
                    onClick={() => onOpen(item.hash)}
                    className={`w-full rounded-lg px-2.5 py-2 text-left transition ${
                      active
                        ? "bg-theme-secondary"
                        : "hover:bg-black hover:bg-opacity-5"
                    }`}
                  >
                    <div className="truncate text-xs font-medium text-theme-text">
                      {item.input_summary || item.hash}
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-[11px] text-theme-text opacity-50">
                      <span
                        className={`inline-flex items-center ${
                          item.status === "succeeded" ? "" : "capitalize"
                        }`}
                      >
                        {item.status === "succeeded" && item.result_count != null
                          ? `${item.result_count} domains`
                          : item.status}
                        {isInFlight(item.status) ? <AnimatedDots /> : null}
                      </span>
                      {ts && <span className="ml-auto flex-shrink-0">{ts}</span>}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
          {runs.length > visibleCount && (
            <button
              type="button"
              onClick={() => setVisibleCount((n) => n + PAST_RUNS_PAGE)}
              className="text-xs font-medium text-theme-primary hover:opacity-80"
            >
              Show older runs
            </button>
          )}
        </>
      )}
    </aside>
  );
}
